import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import {
  ArrowLeft,
  Stethoscope,
  Building2,
  Wrench,
  ClipboardList,
  ArrowDownUp,
  ArrowRight,
  AlertTriangle,
} from 'lucide-react';
import { callAdminGetVetaxisOperations } from '../../services/admin';

type OpsData = {
  totalClinics: number;
  totalUsers: number;
  orphanedUsers: number;
};

const AdminVetAxis: React.FC = () => {
  const [ops, setOps] = useState<OpsData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const data = await callAdminGetVetaxisOperations({ limitClinics: 300, limitUsers: 1500 });
        if (!mounted) return;
        setOps({
          totalClinics: data.totalClinics || 0,
          totalUsers: data.totalUsers || 0,
          orphanedUsers: data.orphanedUsers || 0,
        });
      } catch (err: any) {
        if (!mounted) return;
        setError(err?.message || 'Failed to load VetAxis summary');
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => {
      mounted = false;
    };
  }, []);

  const cards = [
    {
      title: 'Clinics',
      description: 'List all VetAxis clinics and open users linked to each clinic',
      icon: Building2,
      href: '/admin/vetaxis/clinics',
      stat: loading ? '...' : String(ops?.totalClinics ?? 0),
      statLabel: 'Clinics',
    },
    {
      title: 'Backfill Tool',
      description: 'Repair missing users, clinics, and membership links for entitled users',
      icon: Wrench,
      href: '/admin/vetaxis/backfill',
      stat: loading ? '...' : String(ops?.orphanedUsers ?? 0),
      statLabel: 'Users missing clinic links',
    },
    {
      title: 'Sync Logs',
      description: 'Entitlement sync, clinic bootstrap, user identity, and password sync events',
      icon: ArrowDownUp,
      href: '/admin/vetaxis/sync-logs',
      stat: loading ? '...' : '—',
      statLabel: 'View sync trail',
    },
    {
      title: 'Audit Logs',
      description: 'Review VetAxis operational and payment-related events',
      icon: ClipboardList,
      href: '/admin/vetaxis/audit-logs',
      stat: loading ? '...' : String(ops?.totalUsers ?? 0),
      statLabel: 'VetAxis users',
    },
  ];

  return (
    <div className="min-h-screen pt-24 pb-12">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div className="mb-10" initial={{ opacity: 0, y: -20 }} animate={{ opacity: 1, y: 0 }}>
          <Link to="/admin" className="inline-flex items-center gap-2 text-sm text-primary-600 dark:text-primary-400 hover:underline mb-4">
            <ArrowLeft className="w-4 h-4" /> Back to Admin
          </Link>
          <div className="flex items-center gap-3 mb-4">
            <div className="p-2.5 rounded-xl bg-primary-500/10 dark:bg-primary-400/10">
              <Stethoscope className="w-6 h-6 text-primary-600 dark:text-primary-400" />
            </div>
            <div>
              <h1 className="text-3xl md:text-4xl font-bold text-slate-900 dark:text-white">VetAxis Ops</h1>
              <p className="text-slate-600 dark:text-gray-400">Operational tools for clinics, linked users, backfill, and audit visibility</p>
            </div>
          </div>

          {error && (
            <div className="inline-flex items-center gap-2 px-3 py-2 rounded-lg border border-amber-300 bg-amber-50 text-amber-800 dark:border-amber-700 dark:bg-amber-900/20 dark:text-amber-300 text-sm">
              <AlertTriangle className="w-4 h-4" />
              <span>{error}</span>
            </div>
          )}
        </motion.div>

        <motion.div
          className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
        >
          {cards.map((card) => {
            const Icon = card.icon;
            return (
              <Link key={card.href} to={card.href}>
                <motion.div
                  whileHover={{ y: -4 }}
                  className="glass-effect glass-effect-hover rounded-2xl p-6 shadow-glass h-full flex flex-col cursor-pointer"
                >
                  <div className="flex items-start justify-between mb-4">
                    <div className="p-3 rounded-xl bg-primary-500/10 dark:bg-primary-400/10">
                      <Icon className="w-6 h-6 text-primary-600 dark:text-primary-400" />
                    </div>
                    <ArrowRight className="w-5 h-5 text-slate-400" />
                  </div>
                  <h3 className="text-xl font-bold text-slate-900 dark:text-white mb-2">{card.title}</h3>
                  <p className="text-sm text-slate-600 dark:text-gray-400 mb-4 flex-1">{card.description}</p>
                  <div className="pt-4 border-t border-slate-200 dark:border-gray-700">
                    <span className="text-2xl font-bold text-slate-900 dark:text-white">{card.stat}</span>
                    <span className="text-sm text-slate-500 dark:text-gray-400 ml-2">{card.statLabel}</span>
                  </div>
                </motion.div>
              </Link>
            );
          })}
        </motion.div>
      </div>
    </div>
  );
};

export default AdminVetAxis;
